import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  Timestamp,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import {
  Protocol,
  ProtocolStatus,
  ProtocolType,
  ProtocolPriority,
} from "../@types/protocol";
import { db } from "./firebase";

const PROTOCOLS_COLLECTION = "users/belacruz@eleg/protocolos";

export const protocolService = {
  // Gerar número de protocolo
  async generateProtocolNumber(): Promise<string> {
    const year = new Date().getFullYear();
    try {
      // Buscar todos os protocolos
      const q = query(collection(db, PROTOCOLS_COLLECTION));
      const querySnapshot = await getDocs(q);

      // Usar o total de documentos como base para o número
      const totalProtocols = querySnapshot.size;
      const newNumber = totalProtocols + 1;

      // Formato: 0000000.00000481/2025
      return `${newNumber.toString().padStart(8, "0")}/${year}`;
    } catch (error) {
      console.error("Erro ao gerar número do protocolo:", error);
      // Em caso de erro, usar timestamp como número
      const timestamp = Date.now();
      return `0000000.${timestamp
        .toString()
        .slice(-8)
        .padStart(8, "0")}/${year}`;
    }
  },

  // Criar novo protocolo
  async createProtocol(
    data: Omit<
      Protocol,
      "id" | "numeroCompleto" | "dataApresentacao" | "dataUltimaTramitacao"
    >
  ): Promise<Protocol> {
    const protocolNumber = await this.generateProtocolNumber();
    const now = new Date();

    const protocolData = {
      ...data,
      numeroCompleto: protocolNumber,
      dataApresentacao: now,
      dataUltimaTramitacao: now,
      status: "Tramitando" as ProtocolStatus,
      tramitacao: {
        faseAtual: "Apresentação",
        historico: [
          {
            fase: "Apresentação",
            status: "Tramitando",
            data: now,
            orgao: {
              id: "1",
              nome: "Mesa Diretora",
              sigla: "MESA",
            },
            observacao: "Protocolo apresentado",
          },
        ],
      },
    };

    const docRef = await addDoc(
      collection(db, PROTOCOLS_COLLECTION),
      protocolData
    );
    const newDoc = await getDoc(docRef);

    return {
      id: docRef.id,
      ...protocolData,
    } as Protocol;
  },

  // Atualizar protocolo
  async updateProtocol(id: string, data: Partial<Protocol>): Promise<void> {
    const docRef = doc(db, PROTOCOLS_COLLECTION, id);
    await updateDoc(docRef, {
      ...data,
      dataUltimaTramitacao: new Date(),
    });
  },

  // Atualizar status do protocolo
  async updateStatus(
    id: string,
    status: ProtocolStatus,
    orgao: { id: string; nome: string; sigla: string },
    fase: string,
    observacao?: string
  ): Promise<void> {
    const docRef = doc(db, PROTOCOLS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    const currentData = docSnap.data();

    const historyEntry = {
      fase,
      status,
      data: new Date(),
      orgao,
      observacao,
    };

    await updateDoc(docRef, {
      status,
      dataUltimaTramitacao: new Date(),
      "tramitacao.faseAtual": fase,
      "tramitacao.historico": [
        ...currentData?.tramitacao.historico,
        historyEntry,
      ],
    });
  },

  // Buscar protocolo por ID
  async getProtocolById(id: string): Promise<Protocol | null> {
    const docRef = doc(db, PROTOCOLS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as Protocol;
  },

  // Listar protocolos com filtros
  async listProtocols({
    status,
    tipo,
    autorId,
    startDate,
    endDate,
    search,
  }: {
    status?: ProtocolStatus;
    tipo?: ProtocolType;
    autorId?: string;
    startDate?: Date;
    endDate?: Date;
    search?: string;
  } = {}): Promise<Protocol[]> {
    let q = query(collection(db, PROTOCOLS_COLLECTION));

    if (status) {
      q = query(q, where("status", "==", status));
    }

    if (tipo) {
      q = query(q, where("tipo", "==", tipo));
    }

    if (autorId) {
      q = query(q, where("autor.id", "==", autorId));
    }

    if (startDate) {
      q = query(
        q,
        where("dataApresentacao", ">=", Timestamp.fromDate(startDate))
      );
    }

    if (endDate) {
      q = query(
        q,
        where("dataApresentacao", "<=", Timestamp.fromDate(endDate))
      );
    }

    // Ordenar por data de apresentação decrescente
    q = query(q, orderBy("dataApresentacao", "desc"));

    const querySnapshot = await getDocs(q);
    const protocols = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Protocol[];

    // Filtrar por texto se necessário
    if (search) {
      const searchLower = search.toLowerCase();
      return protocols.filter(
        (protocol) =>
          protocol.ementa.toLowerCase().includes(searchLower) ||
          protocol.numeroCompleto.toLowerCase().includes(searchLower) ||
          protocol.texto.toLowerCase().includes(searchLower)
      );
    }

    return protocols;
  },

  // Adicionar protocolo a uma sessão
  async addProtocolToSession(
    protocolId: string,
    sessionId: string
  ): Promise<void> {
    await this.updateProtocol(protocolId, {
      sessaoId: sessionId,
      status: "Aguardando Votação",
    });
  },

  // Registrar resultado da votação
  async registerVotingResult(
    protocolId: string,
    result: {
      aprovado: boolean;
      votosFavor: number;
      votosContra: number;
      abstencoes: number;
    }
  ): Promise<void> {
    await this.updateProtocol(protocolId, {
      status: result.aprovado ? "Aprovado" : "Rejeitado",
      resultadoVotacao: {
        ...result,
        data: new Date(),
      },
    });
  },

  // Adicionar protocolo a uma pauta
  async addProtocolToProposition(
    proposicaoId: string,
    protocolData: Omit<
      Protocol,
      | "id"
      | "numeroCompleto"
      | "dataApresentacao"
      | "dataUltimaTramitacao"
      | "proposicaoId"
    >
  ): Promise<Protocol> {
    try {
      // Verificar se a pauta existe e não foi votada
      const pautaRef = doc(db, "users/belacruz@eleg/pautas", proposicaoId);
      const pautaDoc = await getDoc(pautaRef);

      if (!pautaDoc.exists()) {
        throw new Error("Pauta não encontrada");
      }

      const pautaData = pautaDoc.data();
      console.log("Dados da pauta:", pautaData);

      if (pautaData.votada) {
        throw new Error("Esta pauta já foi votada");
      }

      const now = new Date();
      const protocolNumber = await this.generateProtocolNumber();

      const mockAutor = {
        id: "1",
        nome: pautaData.autor,
        siglaPartido: "PT",
        siglaUf: "SP",
        tipo: "Deputado" as const,
      };

      const newProtocol = {
        tipo: "PL" as ProtocolType,
        ementa: pautaData.pautaLegislativa,
        texto: pautaData.expressoes,
        prioridade: "normal" as ProtocolPriority,
        keywords: [],
        autor: mockAutor,
        casa: "CD" as const,
        siglaCasa: "CD",
        nomeCasa: "Câmara dos Deputados",
        siglaTipo: "PL",
        descricaoTipo: "Projeto de Lei",
        ano: now.getFullYear(),
        numero: parseInt(pautaData.numero),
        urlInteiroTeor: "",
        regime: "Ordinário" as const,
        status: "Tramitando" as ProtocolStatus,
        numeroCompleto: protocolNumber,
        dataApresentacao: now,
        dataUltimaTramitacao: now,
        proposicaoId: "", // Será preenchido após a criação da pauta
        tramitacao: {
          faseAtual: "Apresentação",
          historico: [
            {
              fase: "Apresentação",
              status: "Tramitando",
              data: now,
              orgao: {
                id: "1",
                nome: "Mesa Diretora",
                sigla: "MESA",
              },
              observacao: "Protocolo apresentado",
            },
          ],
        },
      };

      console.log("Criando protocolo com dados:", newProtocol);

      try {
        const docRef = await addDoc(
          collection(db, PROTOCOLS_COLLECTION),
          newProtocol
        );
        const newDoc = await getDoc(docRef);

        console.log("Protocolo criado com sucesso:", newDoc.id);

        // Atualizar a pauta com a referência ao novo protocolo
        const protocolosRelacionados = pautaData.protocolosRelacionados || [];
        console.log("Protocolos relacionados atuais:", protocolosRelacionados);

        await updateDoc(pautaRef, {
          protocolosRelacionados: [...protocolosRelacionados, docRef.id],
        });

        console.log("Pauta atualizada com sucesso");

        return {
          id: docRef.id,
          ...newProtocol,
        } as Protocol;
      } catch (error) {
        console.error("Erro ao criar ou atualizar documentos:", error);
        throw new Error(
          `Erro ao salvar o protocolo: ${
            error instanceof Error ? error.message : "Erro desconhecido"
          }`
        );
      }
    } catch (error) {
      console.error("Erro detalhado ao adicionar protocolo à pauta:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Erro desconhecido ao adicionar protocolo à pauta");
    }
  },

  // Criar protocolo automaticamente para uma nova pauta
  async createProtocolForPauta(pautaData: {
    pautaLegislativa: string;
    numero: string;
    autor: string;
    expressoes: string;
  }): Promise<Protocol> {
    try {
      const now = new Date();
      const protocolNumber = await this.generateProtocolNumber();

      const mockAutor = {
        id: "1",
        nome: pautaData.autor,
        siglaPartido: "PT",
        siglaUf: "SP",
        tipo: "Deputado" as const,
      };

      const newProtocol = {
        tipo: "PL" as ProtocolType,
        ementa: pautaData.pautaLegislativa,
        texto: pautaData.expressoes,
        prioridade: "normal" as ProtocolPriority,
        keywords: [],
        autor: mockAutor,
        casa: "CD" as const,
        siglaCasa: "CD",
        nomeCasa: "Câmara dos Deputados",
        siglaTipo: "PL",
        descricaoTipo: "Projeto de Lei",
        ano: now.getFullYear(),
        numero: parseInt(pautaData.numero),
        urlInteiroTeor: "",
        regime: "Ordinário" as const,
        status: "Tramitando" as ProtocolStatus,
        numeroCompleto: protocolNumber,
        dataApresentacao: now,
        dataUltimaTramitacao: now,
        proposicaoId: "", // Será preenchido após a criação da pauta
        tramitacao: {
          faseAtual: "Apresentação",
          historico: [
            {
              fase: "Apresentação",
              status: "Tramitando",
              data: now,
              orgao: {
                id: "1",
                nome: "Mesa Diretora",
                sigla: "MESA",
              },
              observacao: "Protocolo apresentado",
            },
          ],
        },
      };

      const docRef = await addDoc(
        collection(db, PROTOCOLS_COLLECTION),
        newProtocol
      );

      return {
        id: docRef.id,
        ...newProtocol,
      } as Protocol;
    } catch (error) {
      console.error("Erro ao criar protocolo para pauta:", error);
      throw new Error("Erro ao criar protocolo para pauta");
    }
  },
};
